/*eslint complexity: ["error", 27]*/
/*eslint max-lines-per-function: ["error", 420]*/
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Alert, Form, Tab, Tabs } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import { FormSelect } from '../../../app/components/FormSelect';
import {
  CreateUpdateArticleDto,
  CreateUpdateArticleDtoCardTypeEnum,
  CreateUpdateArticleDtoLegalBasisTypeEnum,
  CreateUpdateArticleDtoModeEnum,
  CreateUpdateArticleHarmfulFactorSuppExamDictDto,
  GetArticleHarmfulFactorSuppExamDictDto,
  GetPdfTemplateDto,
  GetRegisterDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import {
  CardType,
  CardTypeText,
  LegalBasisType,
  LegalBasisTypeText,
} from '../../../common/spdCore/validation/schemas';
import PricingGroupMultiSelector from '../PricingGroup/PricingGroupMultiSelector';
import { articleHarmfulFactorSuppExamDictData } from '../../../common/spdCore/contexts/ArticleContext';

import { ArticleHarmfulFactorSuppExamDictEditor } from './ArticleHarmfulFactorSuppExamDictEditor';

interface ArticleEditorProps {
  id?: string;
  article: CreateUpdateArticleDto;
  handleClose?: () => void;
  show?: boolean;
  pdfTemplates: GetPdfTemplateDto[];
  registers: GetRegisterDto[];
  onCompleted: (
    article: CreateUpdateArticleDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ArticleEditor: React.FC<ArticleEditorProps> = props => {
  const [article, setArticle] = useState(props.article);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [initialOverrides, setInitialOverrides] = useState<
    GetArticleHarmfulFactorSuppExamDictDto[]
  >([]);
  const [overridesForSave, setOverridesForSave] = useState<
    CreateUpdateArticleHarmfulFactorSuppExamDictDto[]
  >([]);
  const notifications = useNotifications();
  const spd = useSpdCore();

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setArticle(props.article);
      if (props.id) {
        spd.articles
          .get(props.id)
          .then(a => {
            const data = a.articleHarmfulFactorSuppExamDicts || [];
            setInitialOverrides(data);
            setOverridesForSave(data.map(articleHarmfulFactorSuppExamDictData));
          })
          .catch(() => {
            setInitialOverrides([]);
            setOverridesForSave([]);
          });
      } else {
        setInitialOverrides([]);
        setOverridesForSave([]);
      }
    }
  }, [props.show, props.article, props.id, spd.articles]);

  const createUpdateArticle = async () => {
    const articleWithOverrides: CreateUpdateArticleDto = {
      ...article,
      articleHarmfulFactorSuppExamDicts: overridesForSave,
    };
    props
      .onCompleted(articleWithOverrides, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
      })
      .catch(error => {
        console.error('Error during add article:', error);
      });
  };

  const deleteArticle = async () => {
    if (props.id) {
      notifications.onPromise(
        spd.articles.changeToInactive(props.id),
        props.handleClose,
      );
    }
  };

  const getAllTemplateTypes = () => {
    const arr = props.pdfTemplates.map(pdf => {
      return {
        value: pdf.type,
        label: pdf.name,
      };
    });
    return arr;
  };

  const getSelectedTemplateTypes = () => {
    return getAllTemplateTypes().filter(
      item => item.value === props.article.pdfTemplateType,
    );
  };

  const getSelectedEngTemplateTypes = () => {
    return getAllTemplateTypes().filter(
      item => item.value === props.article.engPdfTemplateType,
    );
  };

  const getAllLegalBasisTypes = () => {
    return Object.keys(LegalBasisType).map(name => ({
      value: LegalBasisType[name as keyof typeof LegalBasisType],
      label: LegalBasisTypeText[name as keyof typeof LegalBasisType],
    }));
  };

  const getSelectedLegalBasisType = () => {
    return getAllLegalBasisTypes().filter(
      item =>
        item.value === (article.legalBasisType as unknown as LegalBasisType),
    );
  };

  const getAllCardTypes = () => {
    const arr = Object.keys(CardType).map(name => {
      return {
        value: CardType[name as keyof typeof CardType],
        label: CardTypeText[name as keyof typeof CardType],
      };
    });
    return arr;
  };

  const getSelectedCardType = () => {
    return getAllCardTypes().filter(
      item => item.value === (props.article.cardType as unknown as CardType),
    );
  };

  const getAllRegisters = () => {
    const arr = props.registers.map(reg => {
      return {
        value: reg.id,
        label: reg.name,
      };
    });
    return arr;
  };

  const getSelectedRegister = () => {
    return getAllRegisters().filter(
      item => item.value === props.article.registerId,
    );
  };

  const propertyChange = (obj: Partial<CreateUpdateArticleDto>) => {
    setArticle({ ...article, ...obj });
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.id ? 'Edycja artykułu' : 'Dodawanie nowego artykułu'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs defaultActiveKey="general" className="mb-3">
          <Tab eventKey="general" title="Ogólne">
            <Form className="d-grid gap-3">
              <FormRow controlId="article_lp" label="Lp">
                <Form.Control
                  type="number"
                  value={article?.lp || 0}
                  onChange={e => propertyChange({ lp: +e.target.value })}
                />
              </FormRow>
              <FormRow controlId="article_description" label="Opis">
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={article?.description || ''}
                  onChange={e =>
                    propertyChange({ description: e.target.value })
                  }
                />
              </FormRow>
              <FormRow controlId="article_legalBasis" label="Podstawa prawna">
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={article?.legalBasis || ''}
                  onChange={e => propertyChange({ legalBasis: e.target.value })}
                />
              </FormRow>
              <FormRow controlId="article_reason" label="Powód">
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={article?.reason || ''}
                  onChange={e => propertyChange({ reason: e.target.value })}
                />
              </FormRow>
              <FormRow
                controlId="article_attention"
                label="Uwaga wyświetlana podczas dodawania orzeczenia"
              >
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={article?.attention || ''}
                  onChange={e => propertyChange({ attention: e.target.value })}
                />
              </FormRow>
              <FormRow controlId="pricingGroup" label="Grupa cenowa">
                <PricingGroupMultiSelector
                  value={article?.pricingGroupId || ''}
                  onChange={e => propertyChange({ pricingGroupId: e.value })}
                />
              </FormRow>
              <FormRow
                controlId="automaticReady"
                label="Można wystawiać automatycznie"
              >
                <Form.Check
                  type="checkbox"
                  checked={article?.automaticReady ? true : false}
                  onChange={e =>
                    propertyChange({ automaticReady: e.target.checked })
                  }
                  disabled={true}
                />
              </FormRow>
            </Form>
          </Tab>
          <Tab eventKey="kind" title="Rodzaj">
            <Form className="d-grid gap-3">
              <FormRow controlId="mode" label="Obsługiwany przez">
                <FormSelect
                  value={article?.mode}
                  options={[
                    { value: 'Doctor', label: 'Lekarz' },
                    { value: 'Psychologist', label: 'Psycholog' },
                  ]}
                  onChange={value =>
                    propertyChange({
                      mode: value as CreateUpdateArticleDtoModeEnum,
                    })
                  }
                  withEmpty={false}
                />
              </FormRow>
              <FormRow controlId="cardType" label="Powiązany typ karty">
                <Select
                  options={getAllCardTypes()}
                  defaultValue={getSelectedCardType()}
                  onChange={options =>
                    propertyChange({
                      cardType:
                        (options?.value as unknown as CreateUpdateArticleDtoCardTypeEnum) ||
                        null,
                    })
                  }
                  isClearable={true}
                />
              </FormRow>
              <FormRow controlId="register" label="Rejestr">
                <Select
                  options={getAllRegisters()}
                  defaultValue={getSelectedRegister()}
                  onChange={options =>
                    propertyChange({ registerId: options?.value })
                  }
                />
              </FormRow>
            </Form>
          </Tab>
          <Tab eventKey="template" title="Szablon">
            <Form className="d-grid gap-3">
              <FormRow
                controlId="article_legalBasisType"
                label="Typ podstawy prawnej (SIM)"
              >
                <Select
                  options={getAllLegalBasisTypes()}
                  value={getSelectedLegalBasisType()}
                  onChange={options =>
                    propertyChange({
                      legalBasisType:
                        (options?.value as unknown as CreateUpdateArticleDtoLegalBasisTypeEnum) ||
                        null,
                    })
                  }
                  isClearable={true}
                  isDisabled={true}
                />
              </FormRow>
              <FormRow
                controlId="article_legalBasisTypeValidFrom"
                label="Typ podstawy prawnej obowiązuje od"
              >
                <Form.Control
                  type="date"
                  value={
                    article?.legalBasisTypeValidFrom
                      ? new Date(article.legalBasisTypeValidFrom)
                          .toISOString()
                          .split('T')[0]
                      : ''
                  }
                  onChange={e =>
                    propertyChange({
                      legalBasisTypeValidFrom: e.target.value
                        ? new Date(e.target.value)
                        : null,
                    })
                  }
                  disabled={true}
                />
              </FormRow>
              <FormRow controlId="template" label="Szablon wydruku">
                <Select
                  options={getAllTemplateTypes()}
                  defaultValue={getSelectedTemplateTypes()}
                  onChange={options =>
                    propertyChange({ pdfTemplateType: options?.value })
                  }
                />
              </FormRow>
              <FormRow
                controlId="engTemplate"
                label="Szablon wydruku (angielski)"
              >
                <Select
                  options={getAllTemplateTypes()}
                  defaultValue={getSelectedEngTemplateTypes()}
                  onChange={options =>
                    propertyChange({ engPdfTemplateType: options?.value })
                  }
                  isClearable={true}
                />
              </FormRow>
            </Form>
          </Tab>
          <Tab eventKey="harmfulFactors" title="Czynniki szkodliwe">
            <FormRow
              controlId="autoSupporting"
              label="Dodaje badania pomocnicze"
            >
              <Form.Check
                type="checkbox"
                checked={article?.autoSupporting ? true : false}
                onChange={e =>
                  propertyChange({ autoSupporting: e.target.checked })
                }
              />
            </FormRow>
            {article?.autoSupporting && (
              <>
                <FormRow
                  controlId="supportingExaminationsOptional"
                  label="Badania pomocnicze opcjonalne"
                >
                  <Form.Check
                    type="checkbox"
                    checked={
                      article?.supportingExaminationsOptional ? true : false
                    }
                    onChange={e =>
                      propertyChange({
                        supportingExaminationsOptional: e.target.checked,
                      })
                    }
                  />
                </FormRow>
                <Alert variant="info" className="mt-3">
                  Poniższa tabela służy wyłącznie do obsługi niestandardowych
                  przypadków, w których artykuł wymaga specjalnych czynników
                  szkodliwych. Dodatkowo można wypełnić specjalne badania
                  pomocnicze dla wybranego czynnika szkodliwego.
                </Alert>
                <ArticleHarmfulFactorSuppExamDictEditor
                  entries={initialOverrides}
                  onChange={setOverridesForSave}
                />
                <Alert variant="warning" className="mt-3">
                  Brak wskazanych czynników szkodliwych powoduje, że artykuł
                  obsługuje wszystkie czynniki (standardowe) i ich badania
                  pomocnicze.
                  <br />
                  Jeśli z powodu czynnika szkodliwego tutaj zdefiniowanego mają
                  wynikać dodatkowe badania pomocnicze, muszą być tutaj
                  zdefiniowane. Nawet jeśli są standardowe.
                </Alert>
              </>
            )}
          </Tab>
        </Tabs>
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="mt-3"
        />
      </Modal.Body>
      <Modal.Footer>
        {props.id && (
          <LastEditorInfo id={props.id} method={spd.articles.getLastEditor} />
        )}
        <Button variant="outline-secondary" onClick={props.handleClose}>
          Zamknij
        </Button>
        {props.id && (
          <ConfirmationButton
            className="mx-1"
            confirmation="Czy na pewno usunąć artykuł?"
            variant="outline-danger"
            onOK={deleteArticle}
          >
            Usuń
          </ConfirmationButton>
        )}
        <Button variant="outline-primary" onClick={createUpdateArticle}>
          Zapisz
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
